import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { ContagemDoCiclo, DetalheInventario } from '../api/inventarios.js';
import { chaveDoProduto, chavesDivergentes, consultarInventario } from '../api/inventarios.js';
import { useConsulta } from '../api/useConsulta.js';
import { iniciaisDoNome } from '../autenticacao/tipos.js';
import { Cartao } from '../componentes/Cartao.js';
import { formatarHora } from '../componentes/datas.js';
import { rotuloDoLocal } from '../componentes/deposito.js';
import { EstadoVazio } from '../componentes/EstadoVazio.js';
import { IconeAtencao, IconeConfere } from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { Resultado } from '../componentes/Resultado.js';
import { quantidadeDeContagens } from './inventarios/formato.js';
import './ConflitoContagem.css';

/**
 * Conflito de contagem (RF-062, RF-063).
 *
 * Contagens offline do mesmo local chegaram ao servidor. Todas ficam
 * registradas e visíveis lado a lado; o sistema não escolhe nenhuma — e o
 * painel ainda não tem por onde registrar a escolha de quem lê, então também
 * não oferece botão que finja resolver.
 *
 * Lê o MESMO ciclo do detalhe (`GET /inventarios/{id}`): o conflito não é outro
 * dado, é outra apresentação. Uma consulta própria precisaria decidir "quais
 * contagens", e escolher já seria resolver.
 */
export function ConflitoContagem() {
  const { id } = useParams();

  const buscar = useMemo(() => (id === undefined ? null : () => consultarInventario(id)), [id]);
  const consulta = useConsulta(buscar);

  return (
    <Pagina
      voltarPara={id === undefined ? '/inventarios' : `/inventarios/${id}`}
      contexto="Inventários · conflito de contagem"
      titulo="Conflito de contagem"
    >
      <Resultado
        consulta={consulta}
        espera="Carregando as contagens…"
        estaVazio={() => false}
        vazio={null}
      >
        {(detalhe) =>
          detalhe.contagens.length > 1 ? (
            <Comparacao detalhe={detalhe} />
          ) : (
            <Cartao semRespiro>
              <EstadoVazio
                tom="neutro"
                icone={<IconeConfere tamanho={28} />}
                titulo={`${rotuloDoLocal(detalhe.codFil, detalhe.depositoErp)} tem uma contagem só.`}
                detalhe="Não há contagens concorrentes para comparar neste ciclo."
              />
            </Cartao>
          )
        }
      </Resultado>
    </Pagina>
  );
}

function Comparacao({ detalhe }: { readonly detalhe: DetalheInventario }) {
  // O diff vem do SERVIDOR: ele conhece a igualdade decimal (`3.5` é `3.500000`)
  // e compara TODAS as contagens, inclusive o item que só uma delas viu.
  const divergentes = chavesDivergentes(detalhe);

  return (
    <div className="conflito">
      <div className="conflito__aviso">
        <span className="conflito__aviso-icone">
          <IconeAtencao tamanho={20} />
        </span>
        <span className="conflito__aviso-texto">
          <strong className="conflito__aviso-titulo">Contagens concorrentes no mesmo local</strong>
          <span>
            São {quantidadeDeContagens(detalhe.contagens.length)} registradas offline que chegaram
            ao servidor. Todas continuam registradas — nenhuma é descartada sem decisão humana.
          </span>
        </span>
        <span className="conflito__local mono">
          {rotuloDoLocal(detalhe.codFil, detalhe.depositoErp)}
        </span>
      </div>

      <div
        className="conflito__colunas"
        // Uma coluna por contagem: três coletores offline no mesmo depósito
        // geram três, e supor duas esconderia a terceira.
        style={{
          gridTemplateColumns: `repeat(${String(detalhe.contagens.length)}, minmax(0, 1fr))`,
        }}
      >
        {detalhe.contagens.map((contagem, indice) => (
          <ColunaContagem
            key={contagem.id}
            contagem={contagem}
            divergentes={divergentes}
            variante={indice % 2 === 0 ? 'primario' : 'secundario'}
          />
        ))}
      </div>

      <div className="conflito__rodape">
        <span className="conflito__nota">
          Linhas destacadas divergem entre as contagens. A escolha entre elas é decisão humana
          (RF-063) e ainda não é registrada pelo painel.
        </span>
      </div>
    </div>
  );
}

/** Procedência: o aparelho e os DOIS relógios — a distância entre eles é o tempo offline. */
function procedencia(contagem: ContagemDoCiclo): string {
  return [
    contagem.dispositivo?.identificador,
    `contado ${formatarHora(contagem.registradoEm)}`,
    `recebido ${formatarHora(contagem.recebidaEm)}`,
  ]
    .filter((parte) => parte !== undefined)
    .join(' · ');
}

function ColunaContagem({
  contagem,
  divergentes,
  variante,
}: {
  readonly contagem: ContagemDoCiclo;
  readonly divergentes: ReadonlySet<string>;
  readonly variante: 'primario' | 'secundario';
}) {
  const operador = contagem.operador?.nome ?? 'Operador não identificado';

  return (
    <section className="conflito__coluna">
      <header className="conflito__cabecalho">
        <span className={`conflito__avatar conflito__avatar--${variante}`} aria-hidden="true">
          {iniciaisDoNome(operador)}
        </span>
        <span className="conflito__identidade">
          <span className="conflito__operador">{operador}</span>
          <span className="conflito__procedencia">{procedencia(contagem)}</span>
        </span>
        <span className="conflito__total">
          <span className="conflito__total-valor">{contagem.totalUnidades}</span>
          <span className="conflito__total-rotulo">unidades · {contagem.totalSkus} SKUs</span>
        </span>
      </header>

      <ul className="conflito__linhas">
        {contagem.itens.map((item) => {
          const diverge = divergentes.has(chaveDoProduto(item.produto, item.derivacao));

          return (
            <li
              key={item.id}
              className={diverge ? 'conflito__linha conflito__linha--diverge' : 'conflito__linha'}
            >
              <span className="mono">{item.produto}</span>
              {/* Código e derivação, sem descrição: o banco não guarda o nome do produto. */}
              <span className="conflito__derivacao">
                {item.derivacao.trim() === '' ? '—' : item.derivacao}
              </span>
              <span className={diverge ? 'conflito__qtd conflito__qtd--diverge' : 'conflito__qtd'}>
                {item.quantidade}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
